import { Card, DatePicker, Form, Select, Skeleton } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-google-charts";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { columnChartConfigurationOptions } from "../../config";
import { disableFutureDates, formatDate } from "../../services/util";

const { Option } = Select;
const { RangePicker } = DatePicker;

const chartByOption = [
    { key: "year", title: "Year" },
    { key: "days", title: "Day" },
];
function CommitChart({ projects, users }) {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [yearSelected, setYearSelected] = useState(moment());
    const [dateRangeSelected, setDateRangeSelected] = useState([
        moment().subtract(7, "day"),
        moment(),
    ]);
    const [form] = Form.useForm();
    const [chartData, setChartData] = useState([]);
    const [formData, setFormData] = useState({
        staff_id,
        range_year: yearSelected.get("year"),
        project_id: "-1",
        user_id: "",
        chart_by: chartByOption[0].key,
    });
    const [loader, setLoader] = useState(true);

    const {
        status: commitsStatus,
        data: commitsRes,
        refetch: getCommit,
    } = useQuery(
        "/gitCommit",
        async () => {
            return await HTTP("/analytics/getGitCommitsByMonth", {
                ...formData,
            });
        },
        { enabled: false },
    );

    useEffect(() => {
        getCommit();
        form.setFieldsValue({
            chart_by: chartByOption[0].key,
            range_year: yearSelected,
        });
    }, []);

    useEffect(() => {
        setLoader(true);
        getCommit();
    }, [formData]);

    useEffect(() => {
        if (commitsStatus === "success") {
            setLoader(false);
            setChartData(commitsRes.data);
        }
    }, [commitsStatus, commitsRes]);

    function handleYearChange(date, dateString) {
        const yearSelected = dateString || moment().get("year");
        setYearSelected(dateString === "" ? moment() : moment(dateString));
        const updatedFormData = {
            ...formData,
            range_year: yearSelected,
        };
        setFormData(updatedFormData);
    }

    function handleProjectSelectionChange(e) {
        let project_id = -1;
        if (e !== undefined) {
            [project_id] = e.split("#");
        }

        const updatedFormData = {
            ...formData,
            project_id,
        };
        setFormData(updatedFormData);
    }

    function handleUserSelectionChange(user_id) {
        const updatedFormData = {
            ...formData,
            user_id,
        };
        setFormData(updatedFormData);
    }

    function handleChartBySelection(chart_by) {
        const updatedFormData = {
            ...formData,
            chart_by,
        };
        if (chart_by == "year") {
            updatedFormData.range_year = yearSelected.get("year");
            delete updatedFormData["start_date"];
            delete updatedFormData["end_date"];
            form.setFieldsValue({
                range_year: yearSelected,
            });
        } else {
            delete updatedFormData["range_year"];
            const [start_date, end_date] = dateRangeSelected;
            updatedFormData.start_date = formatDate(start_date);
            updatedFormData.end_date = formatDate(end_date);
            form.setFieldsValue({
                range_date: dateRangeSelected,
            });
        }
        setFormData(updatedFormData);
    }

    function handleDateRangeChange(e) {
        let start_date =
            moment(e[0]) < moment()
                ? moment(e[0]).startOf("day")
                : moment().startOf("day");
        let end_date =
            moment(e[1]) < moment()
                ? moment(e[1]).startOf("day")
                : moment().startOf("day");
        setDateRangeSelected([start_date, end_date]);

        form.setFieldsValue({
            range_date: [start_date, end_date],
        });
        const updatedFormData = {
            ...formData,
        };

        updatedFormData.start_date = formatDate(start_date);
        updatedFormData.end_date = formatDate(end_date);
        setFormData(updatedFormData);
    }
    function disabledDate(current) {
        return (
            (current && current < moment().subtract(90, "day")) ||
            current > moment().endOf("day")
        );
    }
    return (
        <Card className="box-shadow margin-10">
            <Form
                form={form}
                labelCol={{
                    span: 8,
                }}
                wrapperCol={{
                    span: 16,
                }}
            >
                <Form.Item label="Project" name="project_id">
                    <Select
                        id="project_id"
                        allowClear
                        showSearch
                        placeholder="Project"
                        loading={projects.length === 0 ? true : false}
                        onChange={handleProjectSelectionChange}
                        style={{ marginLeft: "0px" }}
                    >
                        {projects.map((e) => (
                            <Option key={`${e.project_id}#${e.project_name}`}>
                                {e.project_name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="User" name="user_id">
                    <Select
                        id="user_id"
                        allowClear
                        showSearch
                        mode="multiple"
                        placeholder="User"
                        loading={users.length === 0 ? true : false}
                        style={{ width: "20%" }}
                        onChange={handleUserSelectionChange}
                    >
                        {users.map((e) => (
                            <Option key={`${e.committer_email}`}>
                                {e.committer_name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Chart By" name="chart_by">
                    <Select id="chart_by" onChange={handleChartBySelection}>
                        {chartByOption.map((e) => (
                            <Option key={e.key} value={e.key}>
                                {e.title}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.chart_by !== currentValues.chart_by
                    }
                >
                    {({ getFieldValue }) =>
                        getFieldValue("chart_by") == "year" ? (
                            <Form.Item
                                label="Year"
                                name="range_year"
                                labelCol={{
                                    span: 12,
                                }}
                                wrapperCol={{
                                    span: 8,
                                }}
                            >
                                <DatePicker
                                    disabledDate={disableFutureDates}
                                    id="range_year"
                                    onChange={handleYearChange}
                                    picker="year"
                                    allowClear={false}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label="Date"
                                name="range_date"
                                labelCol={{
                                    span: 12,
                                }}
                                wrapperCol={{
                                    span: 8,
                                }}
                            >
                                <RangePicker
                                    disabledDate={disabledDate}
                                    id="range_date"
                                    onChange={handleDateRangeChange}
                                    format="MM/DD/YYYY"
                                    allowClear={false}
                                    ranges={{
                                        Today: [moment(), moment()],
                                        "Current Month": [
                                            moment().startOf("month"),
                                            moment(),
                                        ],
                                        "Last Month": [
                                            moment()
                                                .subtract(1, "month")
                                                .startOf("month"),
                                            moment()
                                                .subtract(1, "month")
                                                .endOf("month"),
                                        ],
                                    }}
                                />
                            </Form.Item>
                        )
                    }
                </Form.Item>
            </Form>

            {!loader ? (
                <Chart
                    height="50vh"
                    className="charts"
                    chartType="ColumnChart"
                    data={chartData}
                    options={{
                        ...columnChartConfigurationOptions,
                        title: "Commits",
                    }}
                />
            ) : (
                <Skeleton />
            )}
        </Card>
    );
}
export default CommitChart;
